import { FormEvent, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    CheckCircle2,
    CheckSquare,
    Copy,
    HelpCircle,
    Loader2,
    Plus,
    RefreshCw,
    Square,
    Trash2,
    XCircle
} from "lucide-react";
import {
    useActivateTenant,
    useChangePlan,
    useCreateTenantUser,
    useDeleteTenantUser,
    useDnsStatus,
    useIdentityTenants,
    useInvoices,
    useMarkInvoicePaid,
    useResetTenantUserPassword,
    useSetIdentityLink,
    useSuspendTenant,
    useTenant,
    useTenantUsers,
    useLicenseSummary,
    useUpdateTenant,
    useVerifyDns
} from "@/api/hooks";
import {
    type DnsRecord,
    type DnsRecordStatus,
    type InvoiceStatus,
    type TenantPlan,
    type TenantStatus
} from "@/api/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

// ── Badges ────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TenantStatus }) {
    const styles: Record<TenantStatus, string> = {
        Active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        Pending: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
        Suspended: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
        Cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", styles[status])}>
            {status}
        </span>
    );
}

function PlanBadge({ plan }: { plan: TenantPlan }) {
    const styles: Record<TenantPlan, string> = {
        Starter: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
        Business: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
        Enterprise: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium", styles[plan])}>
            {plan}
        </span>
    );
}

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
    const styles: Record<InvoiceStatus, string> = {
        PendingPayment: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
        Paid: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
        Cancelled: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
    };
    const labels: Record<InvoiceStatus, string> = {
        PendingPayment: "Pending Payment",
        Paid: "Paid",
        Cancelled: "Cancelled"
    };
    return (
        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", styles[status])}>
            {labels[status]}
        </span>
    );
}

function DnsStatusIcon({ status }: { status: DnsRecordStatus }) {
    if (status === "Verified") return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
    if (status === "Failed") return <XCircle className="h-4 w-4 text-destructive" />;
    return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
}

// ── Formatters ─────────────────────────────────────────────────────────────────

function formatDate(value: string | null | undefined) {
    if (!value) return "—";
    return new Date(value).toLocaleDateString(undefined, { dateStyle: "medium" });
}

function formatCurrency(amount: number, currency = "USD") {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount);
}

// ── Card wrapper ──────────────────────────────────────────────────────────────

function Card({ title, children, actions }: { title: string; children: React.ReactNode; actions?: React.ReactNode }) {
    return (
        <section className="rounded-lg border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-5 py-3.5">
                <h2 className="font-semibold">{title}</h2>
                {actions}
            </div>
            <div className="p-5">{children}</div>
        </section>
    );
}

// ── Field display ─────────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div>
            <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 text-sm">{value ?? "—"}</dd>
        </div>
    );
}

// ── DNS section ───────────────────────────────────────────────────────────────

function DnsSection({ tenantId }: { tenantId: string }) {
    const { data: dns, isLoading } = useDnsStatus(tenantId);
    const verifyMutation = useVerifyDns();

    function copyToClipboard(text: string) {
        void navigator.clipboard.writeText(text).then(() => alert("Copied to clipboard!"));
    }

    function handleVerify() {
        verifyMutation.mutate(tenantId, {
            onError: (err) => alert(`Verify failed: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    const records: DnsRecord[] = dns?.records ?? [];
    const failCount = records.filter((r) => r.status !== "Verified").length;

    return (
        <Card
            title="DNS Configuration"
            actions={
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleVerify}
                    disabled={verifyMutation.isPending}
                >
                    {verifyMutation.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5" />}
                    Verify DNS
                </Button>
            }
        >
            {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading DNS records…</p>
            ) : !dns ? (
                <p className="text-sm text-muted-foreground">No DNS data available. Click "Verify DNS" to check.</p>
            ) : (
                <div className="space-y-4">
                    {/* Status summary */}
                    {dns.allVerified ? (
                        <div className="flex items-center gap-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
                            <CheckCircle2 className="h-4 w-4 shrink-0" />
                            All records verified — last checked {formatDate(dns.checkedAt)}
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
                            <XCircle className="h-4 w-4 shrink-0" />
                            {failCount} record{failCount !== 1 ? "s" : ""} need attention — last checked {formatDate(dns.checkedAt)}
                        </div>
                    )}

                    {/* Records table */}
                    <div className="overflow-x-auto rounded-md border">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left font-medium text-muted-foreground">
                                    <th className="px-3 py-2">Type</th>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Expected Value</th>
                                    <th className="px-3 py-2">Actual Value</th>
                                    <th className="px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {records.map((r, i) => (
                                    <tr key={i} className="border-b last:border-0 hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono font-semibold">{r.recordType}</td>
                                        <td className="px-3 py-2 font-mono">{r.name}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <span className="max-w-[220px] truncate font-mono" title={r.expectedValue}>
                                                    {r.expectedValue}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => copyToClipboard(r.expectedValue)}
                                                    className="shrink-0 rounded p-0.5 hover:bg-accent"
                                                    aria-label="Copy expected value"
                                                >
                                                    <Copy className="h-3 w-3 text-muted-foreground" />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 font-mono text-muted-foreground">
                                            {r.actualValue ?? "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1">
                                                <DnsStatusIcon status={r.status} />
                                                <span>{r.status}</span>
                                            </div>
                                            {r.errorMessage && (
                                                <p className="mt-0.5 text-destructive">{r.errorMessage}</p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </Card>
    );
}

// ── Identity link section ─────────────────────────────────────────────────────

function IdentityLinkSection({
    tenantId,
    currentIdentityTenantId
}: {
    tenantId: string;
    currentIdentityTenantId: string | null | undefined;
}) {
    const { data: identityTenants, isLoading } = useIdentityTenants();
    const linkMutation = useSetIdentityLink();
    const [selected, setSelected] = useState<string>(currentIdentityTenantId ?? "");
    const [saved, setSaved] = useState(false);

    const isDirty = selected !== (currentIdentityTenantId ?? "");

    function handleSave() {
        setSaved(false);
        linkMutation.mutate(
            { portalTenantId: tenantId, identityTenantId: selected || null },
            {
                onSuccess: () => setSaved(true),
                onError: (err) => alert(`Link failed: ${err instanceof Error ? err.message : String(err)}`)
            }
        );
    }

    const current = identityTenants?.find((t) => t.tenantId === (currentIdentityTenantId ?? ""));

    return (
        <Card title="Identity Service Link">
            <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                    Link this portal tenant to the matching identity-service tenant so user management works.
                    {current && (
                        <span className="ml-1 font-medium text-foreground">
                            Currently linked to: <span className="font-mono">{current.name}</span> ({current.subdomain})
                        </span>
                    )}
                    {!currentIdentityTenantId && (
                        <span className="ml-1 text-amber-600 dark:text-amber-400">Not linked — user management will use domain matching.</span>
                    )}
                </p>
                <div className="flex items-center gap-2">
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    ) : (
                        <select
                            value={selected}
                            onChange={(e) => { setSelected(e.target.value); setSaved(false); }}
                            className="h-9 flex-1 rounded-md border bg-background px-3 text-sm"
                        >
                            <option value="">(none — use domain matching)</option>
                            {(identityTenants ?? []).map((t) => (
                                <option key={t.tenantId} value={t.tenantId}>
                                    {t.name} — {t.subdomain}{t.mailDomain ? ` / ${t.mailDomain}` : ""}
                                </option>
                            ))}
                        </select>
                    )}
                    <Button
                        size="sm"
                        disabled={!isDirty || linkMutation.isPending}
                        onClick={handleSave}
                    >
                        {linkMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                    </Button>
                </div>
                {saved && (
                    <p className="text-xs text-green-600 dark:text-green-400">Link saved.</p>
                )}
            </div>
        </Card>
    );
}

// ── Users section ─────────────────────────────────────────────────────────────

function UsersSection({ tenantId }: { tenantId: string }) {
    const { data: users, isLoading } = useTenantUsers(tenantId);
    const createMutation = useCreateTenantUser();
    const resetMutation = useResetTenantUserPassword();
    const deleteMutation = useDeleteTenantUser();

    const [showAdd, setShowAdd] = useState(false);
    const [newEmail, setNewEmail] = useState("");
    const [newName, setNewName] = useState("");
    const [newPass, setNewPass] = useState("");
    const [newRole, setNewRole] = useState("User");
    const [addError, setAddError] = useState<string | null>(null);

    const [resetUserId, setResetUserId] = useState<string | null>(null);
    const [resetPass, setResetPass] = useState("");
    const [resetError, setResetError] = useState<string | null>(null);

    function handleAdd(e: FormEvent) {
        e.preventDefault();
        setAddError(null);
        createMutation.mutate(
            { tenantId, data: { email: newEmail, displayName: newName, password: newPass, role: newRole } },
            {
                onSuccess: () => {
                    setShowAdd(false);
                    setNewEmail(""); setNewName(""); setNewPass(""); setNewRole("User");
                },
                onError: (err) => setAddError(err instanceof Error ? err.message : String(err))
            }
        );
    }

    function handleReset(e: FormEvent) {
        e.preventDefault();
        if (!resetUserId) return;
        setResetError(null);
        resetMutation.mutate(
            { tenantId, userId: resetUserId, newPassword: resetPass },
            {
                onSuccess: () => { setResetUserId(null); setResetPass(""); },
                onError: (err) => setResetError(err instanceof Error ? err.message : String(err))
            }
        );
    }

    function handleDelete(userId: string, email: string) {
        if (!confirm(`Delete user ${email}? This cannot be undone.`)) return;
        deleteMutation.mutate(
            { tenantId, userId },
            { onError: (err) => alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`) }
        );
    }

    return (
        <Card
            title="Users"
            actions={
                <Button variant="outline" size="sm" onClick={() => setShowAdd(true)}>
                    <Plus className="h-3.5 w-3.5" /> Add User
                </Button>
            }
        >
            {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading users…</p>
            ) : !users || users.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users yet. Click "Add User" to create the first one.</p>
            ) : (
                <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                                <th className="px-3 py-2">Email</th>
                                <th className="px-3 py-2">Name</th>
                                <th className="px-3 py-2">Role</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.userId} className="border-b last:border-0 hover:bg-muted/20">
                                    <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                                    <td className="px-3 py-2">{u.displayName ?? "—"}</td>
                                    <td className="px-3 py-2">
                                        <span className="rounded bg-muted px-1.5 py-0.5 text-xs">
                                            {u.roles.join(", ") || "User"}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2">
                                        {u.isLockedOut ? (
                                            <span className="text-xs text-destructive">Locked</span>
                                        ) : (
                                            <span className="text-xs text-green-600 dark:text-green-400">Active</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2">
                                        <div className="flex gap-1">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="h-7 text-xs"
                                                onClick={() => { setResetUserId(u.userId); setResetPass(""); setResetError(null); }}
                                            >
                                                Reset Password
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 text-destructive hover:text-destructive"
                                                onClick={() => handleDelete(u.userId, u.email)}
                                                disabled={deleteMutation.isPending}
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add user dialog */}
            {showAdd && (
                <div
                    role="dialog"
                    aria-label="Add user"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget && !createMutation.isPending) { setShowAdd(false); setAddError(null); } }}
                >
                    <div className="w-full max-w-sm rounded-lg border bg-card p-5 shadow-2xl">
                        <h2 className="mb-4 text-base font-semibold">Add User</h2>
                        <form onSubmit={(e) => void handleAdd(e)} className="space-y-3">
                            <div className="flex flex-col gap-1">
                                <Label htmlFor="newEmail">Email</Label>
                                <Input id="newEmail" type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor="newName">Display Name</Label>
                                <Input id="newName" required value={newName} onChange={(e) => setNewName(e.target.value)} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor="newPass">Password</Label>
                                <Input id="newPass" type="password" required minLength={12} value={newPass} onChange={(e) => setNewPass(e.target.value)} />
                                <p className="text-xs text-muted-foreground">Min 12 chars, must include uppercase, digit, and symbol</p>
                            </div>
                            <div className="flex flex-col gap-1">
                                <Label htmlFor="newRole">Role</Label>
                                <select
                                    id="newRole"
                                    value={newRole}
                                    onChange={(e) => setNewRole(e.target.value)}
                                    className="h-9 rounded-md border bg-background px-3 text-sm"
                                >
                                    <option value="User">User</option>
                                    <option value="Admin">Admin</option>
                                </select>
                            </div>
                            {addError && (
                                <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">{addError}</p>
                            )}
                            <div className="flex justify-end gap-2 pt-1">
                                <Button type="button" variant="outline" size="sm" onClick={() => { setShowAdd(false); setAddError(null); }} disabled={createMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button type="submit" size="sm" disabled={createMutation.isPending}>
                                    {createMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reset password dialog */}
            {resetUserId && (
                <div
                    role="dialog"
                    aria-label="Reset password"
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget && !resetMutation.isPending) { setResetUserId(null); setResetError(null); } }}
                >
                    <div className="w-full max-w-sm rounded-lg border bg-card p-5 shadow-2xl">
                        <h2 className="mb-4 text-base font-semibold">Reset Password</h2>
                        <form onSubmit={(e) => void handleReset(e)} className="space-y-3">
                            <div className="flex flex-col gap-1">
                                <Label htmlFor="resetPass">New Password</Label>
                                <Input id="resetPass" type="password" required minLength={12} value={resetPass} onChange={(e) => setResetPass(e.target.value)} />
                                <p className="text-xs text-muted-foreground">Min 12 chars, must include uppercase, digit, and symbol</p>
                            </div>
                            {resetError && (
                                <p className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">{resetError}</p>
                            )}
                            <div className="flex justify-end gap-2 pt-1">
                                <Button type="button" variant="outline" size="sm" onClick={() => { setResetUserId(null); setResetError(null); }} disabled={resetMutation.isPending}>
                                    Cancel
                                </Button>
                                <Button type="submit" size="sm" disabled={resetMutation.isPending}>
                                    {resetMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reset"}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Card>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function TenantDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { data: tenant, isLoading, error } = useTenant(id);
    const { data: invoices } = useInvoices(undefined, id);
    const { data: license } = useLicenseSummary(id);
    const updateMutation = useUpdateTenant();
    const activateMutation = useActivateTenant();
    const suspendMutation = useSuspendTenant();
    const markPaidMutation = useMarkInvoicePaid();
    const [planDialogOpen, setPlanDialogOpen] = useState(false);

    // Edit state
    const [isEditing, setIsEditing] = useState(false);
    const [editValues, setEditValues] = useState({
        companyName: "",
        adminEmail: "",
        adminName: "",
        phone: "",
        address: "",
        notes: ""
    });

    const startEdit = useCallback(() => {
        if (!tenant) return;
        setEditValues({
            companyName: tenant.companyName,
            adminEmail: tenant.adminEmail,
            adminName: tenant.adminName,
            phone: tenant.phone ?? "",
            address: tenant.address ?? "",
            notes: tenant.notes ?? ""
        });
        setIsEditing(true);
    }, [tenant]);

    useEffect(() => {
        if (tenant && !isEditing) {
            setEditValues({
                companyName: tenant.companyName,
                adminEmail: tenant.adminEmail,
                adminName: tenant.adminName,
                phone: tenant.phone ?? "",
                address: tenant.address ?? "",
                notes: tenant.notes ?? ""
            });
        }
    }, [tenant, isEditing]);

    function handleSave(e: FormEvent) {
        e.preventDefault();
        if (!id) return;
        updateMutation.mutate(
            {
                id,
                data: {
                    companyName: editValues.companyName,
                    adminEmail: editValues.adminEmail,
                    adminName: editValues.adminName,
                    phone: editValues.phone || null,
                    address: editValues.address || null,
                    notes: editValues.notes || null
                }
            },
            {
                onSuccess: () => setIsEditing(false),
                onError: (err) => alert(`Save failed: ${err instanceof Error ? err.message : "Unknown error"}`)
            }
        );
    }

    function handleActivate() {
        if (!id) return;
        activateMutation.mutate(id, {
            onError: (err) => alert(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    function handleSuspend() {
        if (!id) return;
        if (!confirm("Suspend this tenant?")) return;
        suspendMutation.mutate(id, {
            onError: (err) => alert(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    function handleMarkPaid(invoiceId: string) {
        markPaidMutation.mutate(invoiceId, {
            onSuccess: () => alert("Invoice marked as paid."),
            onError: (err) => alert(`Failed: ${err instanceof Error ? err.message : "Unknown error"}`)
        });
    }

    if (isLoading) {
        return (
            <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-32 animate-pulse rounded-lg border bg-muted" />
                ))}
            </div>
        );
    }

    if (error || !tenant) {
        return (
            <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                {error instanceof Error ? error.message : "Tenant not found"}
            </div>
        );
    }

    const allApps = ["Mail", "Calendar", "Docs", "Sheets", "Drive", "Teams"];

    return (
        <div className="mx-auto max-w-5xl space-y-5">
            {/* Back + header */}
            <div className="flex items-start gap-4">
                <Button variant="ghost" size="sm" asChild className="-ml-1 shrink-0">
                    <Link to="/tenants"><ArrowLeft className="h-4 w-4" /> Tenants</Link>
                </Button>
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold">{tenant.companyName}</h1>
                        <StatusBadge status={tenant.status} />
                        <PlanBadge plan={tenant.plan} />
                    </div>
                    <p className="mt-0.5 font-mono text-sm text-muted-foreground">{tenant.domain}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                    {(tenant.status === "Pending" || tenant.status === "Suspended") && (
                        <Button
                            size="sm"
                            onClick={handleActivate}
                            disabled={activateMutation.isPending}
                            className="bg-green-700 hover:bg-green-800 text-white"
                        >
                            {activateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Activate"}
                        </Button>
                    )}
                    {tenant.status === "Active" && (
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleSuspend}
                            disabled={suspendMutation.isPending}
                        >
                            {suspendMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Suspend"}
                        </Button>
                    )}
                </div>
            </div>

            {/* Office 365–style Licenses widget */}
            {license ? (
                <Card
                    title="Licenses"
                    actions={
                        tenant.status !== "Cancelled" ? (
                            <Button variant="outline" size="sm" onClick={() => setPlanDialogOpen(true)}>
                                Change plan
                            </Button>
                        ) : null
                    }
                >
                    <div className="space-y-3 p-1">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <div>
                                <p className="text-2xl font-semibold leading-tight">
                                    {license.usedSeats}{" "}
                                    <span className="text-base font-normal text-muted-foreground">
                                        of {license.totalSeats} seats used
                                    </span>
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {license.plan} · {license.currency} {license.pricePerSeatMonthly.toFixed(0)}/seat/month
                                </p>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {license.freeSeats} available
                            </p>
                        </div>
                        <div
                            className="h-2 w-full overflow-hidden rounded-full bg-muted"
                            role="progressbar"
                            aria-valuenow={license.usedSeats}
                            aria-valuemin={0}
                            aria-valuemax={license.totalSeats || 1}
                        >
                            <div
                                style={{
                                    width: `${license.totalSeats > 0
                                        ? Math.min(100, (license.usedSeats / license.totalSeats) * 100)
                                        : 0}%`
                                }}
                                className={
                                    license.usedSeats >= license.totalSeats
                                        ? "h-full bg-destructive"
                                        : license.totalSeats > 0 && license.usedSeats / license.totalSeats > 0.85
                                            ? "h-full bg-amber-500"
                                            : "h-full bg-primary"
                                }
                            />
                        </div>
                        {license.enabledApps.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {license.enabledApps.map((app) => (
                                    <span
                                        key={app}
                                        className="rounded bg-primary/10 px-2 py-0.5 text-xs text-primary"
                                    >
                                        {app}
                                    </span>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </Card>
            ) : null}

            {/* Section 1 — Tenant Info */}
            <Card
                title="Tenant Information"
                actions={
                    isEditing ? null : (
                        <Button variant="outline" size="sm" onClick={startEdit}>Edit</Button>
                    )
                }
            >
                {isEditing ? (
                    <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="companyName">Company Name</Label>
                                <Input
                                    id="companyName"
                                    required
                                    value={editValues.companyName}
                                    onChange={(e) => setEditValues((p) => ({ ...p, companyName: e.target.value }))}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="adminEmail">Admin Email</Label>
                                <Input
                                    id="adminEmail"
                                    type="email"
                                    required
                                    value={editValues.adminEmail}
                                    onChange={(e) => setEditValues((p) => ({ ...p, adminEmail: e.target.value }))}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="adminName">Admin Name</Label>
                                <Input
                                    id="adminName"
                                    required
                                    value={editValues.adminName}
                                    onChange={(e) => setEditValues((p) => ({ ...p, adminName: e.target.value }))}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label htmlFor="phone">Phone</Label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={editValues.phone}
                                    onChange={(e) => setEditValues((p) => ({ ...p, phone: e.target.value }))}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <Label htmlFor="address">Address</Label>
                                <Input
                                    id="address"
                                    value={editValues.address}
                                    onChange={(e) => setEditValues((p) => ({ ...p, address: e.target.value }))}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 sm:col-span-2">
                                <Label htmlFor="notes">Notes</Label>
                                <Textarea
                                    id="notes"
                                    rows={3}
                                    value={editValues.notes}
                                    onChange={(e) => setEditValues((p) => ({ ...p, notes: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm" disabled={updateMutation.isPending}>
                                {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save changes"}
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                                Cancel
                            </Button>
                        </div>
                    </form>
                ) : (
                    <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <Field label="Company Name" value={tenant.companyName} />
                        <Field label="Domain" value={<span className="font-mono">{tenant.domain}</span>} />
                        <Field label="Admin Email" value={tenant.adminEmail} />
                        <Field label="Admin Name" value={tenant.adminName} />
                        <Field label="Phone" value={tenant.phone} />
                        <Field label="Address" value={tenant.address} />
                        <Field label="Created" value={formatDate(tenant.createdAt)} />
                        <Field label="Activated" value={formatDate(tenant.activatedAt)} />
                        <Field label="Updated" value={formatDate(tenant.updatedAt)} />
                        {tenant.notes && (
                            <div className="sm:col-span-2 lg:col-span-3">
                                <Field label="Notes" value={tenant.notes} />
                            </div>
                        )}
                    </dl>
                )}
            </Card>

            {/* Section 2 — License & Apps */}
            <Card title="License & Applications">
                <div className="space-y-4">
                    <dl className="grid gap-4 sm:grid-cols-3">
                        <Field label="Plan" value={<PlanBadge plan={tenant.plan} />} />
                        <Field label="Max Users" value={tenant.maxUsers ?? "Unlimited"} />
                        <Field label="Domain Verified" value={formatDate(tenant.domainVerifiedAt)} />
                    </dl>
                    <div>
                        <p className="mb-2 text-xs font-medium text-muted-foreground">Enabled Applications</p>
                        <div className="flex flex-wrap gap-2">
                            {allApps.map((app) => {
                                const enabled = tenant.enabledApps.includes(app);
                                return (
                                    <span
                                        key={app}
                                        className={cn(
                                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium",
                                            enabled
                                                ? "border-primary/30 bg-primary/10 text-primary"
                                                : "border-muted bg-muted/40 text-muted-foreground"
                                        )}
                                    >
                                        {enabled
                                            ? <CheckSquare className="h-3.5 w-3.5" />
                                            : <Square className="h-3.5 w-3.5" />}
                                        {app}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Section 3 — Identity link */}
            {id && (
                <IdentityLinkSection
                    tenantId={id}
                    currentIdentityTenantId={tenant.identityTenantId}
                />
            )}

            {/* Section 4 — DNS */}
            {id && <DnsSection tenantId={id} />}

            {/* Section 5 — Users */}
            {id && <UsersSection tenantId={id} />}

            {/* Section 6 — Invoices */}
            <Card title="Invoices">
                {!invoices || invoices.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No invoices for this tenant.</p>
                ) : (
                    <div className="overflow-x-auto rounded-md border">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b bg-muted/40 text-left text-xs font-medium text-muted-foreground">
                                    <th className="px-3 py-2">Invoice #</th>
                                    <th className="px-3 py-2">Amount</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Due Date</th>
                                    <th className="px-3 py-2">Paid At</th>
                                    <th className="px-3 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((inv) => (
                                    <tr key={inv.id} className="border-b last:border-0 hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">{inv.invoiceNumber}</td>
                                        <td className="px-3 py-2 tabular-nums">{formatCurrency(inv.amount, inv.currency)}</td>
                                        <td className="px-3 py-2"><InvoiceStatusBadge status={inv.status} /></td>
                                        <td className="px-3 py-2 text-muted-foreground">{formatDate(inv.dueDate)}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{formatDate(inv.paidAt)}</td>
                                        <td className="px-3 py-2">
                                            {inv.status === "PendingPayment" && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleMarkPaid(inv.id)}
                                                    disabled={markPaidMutation.isPending}
                                                >
                                                    {markPaidMutation.isPending
                                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                        : "Mark Paid"}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {planDialogOpen && tenant ? (
                <ChangePlanDialog
                    tenantId={tenant.id}
                    currentPlan={tenant.plan}
                    currentMaxUsers={tenant.maxUsers ?? 1}
                    onClose={() => setPlanDialogOpen(false)}
                />
            ) : null}
        </div>
    );
}

// ── Plan-change dialog ───────────────────────────────────────────────────────

const PLAN_PRICES: Record<TenantPlan, number> = { Starter: 9, Business: 15, Enterprise: 25 };

function ChangePlanDialog({
    tenantId,
    currentPlan,
    currentMaxUsers,
    onClose
}: {
    tenantId: string;
    currentPlan: TenantPlan;
    currentMaxUsers: number;
    onClose: () => void;
}) {
    const [plan, setPlan] = useState<TenantPlan>(currentPlan);
    const [maxUsers, setMaxUsers] = useState<number>(currentMaxUsers);
    const [error, setError] = useState<string | null>(null);
    const mutation = useChangePlan();

    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const daysLeft = Math.max(1, daysInMonth - today.getDate() + 1);
    const oldMonthly = PLAN_PRICES[currentPlan] * currentMaxUsers;
    const newMonthly = PLAN_PRICES[plan] * maxUsers;
    const proratedDelta = ((newMonthly - oldMonthly) * daysLeft) / daysInMonth;
    const noChange = plan === currentPlan && maxUsers === currentMaxUsers;

    const submit = async () => {
        setError(null);
        try {
            await mutation.mutateAsync({ id: tenantId, data: { plan, maxUsers } });
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <div
            role="dialog"
            aria-label="Change plan"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !mutation.isPending) onClose(); }}
        >
            <div className="w-full max-w-md rounded-lg border bg-card p-5 shadow-2xl">
                <h2 className="mb-1 text-base font-semibold">Change plan</h2>
                <p className="mb-4 text-xs text-muted-foreground">
                    Updates the seat pool immediately. A prorated invoice covers the remaining
                    {" "}{daysLeft} day{daysLeft === 1 ? "" : "s"} of this billing month.
                </p>

                <div className="mb-4 grid grid-cols-3 gap-2">
                    {(["Starter", "Business", "Enterprise"] as const).map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setPlan(p)}
                            className={cn(
                                "rounded-md border p-2 text-center text-sm transition-colors",
                                plan === p ? "border-primary bg-primary/5 font-semibold" : "hover:bg-accent"
                            )}
                        >
                            <div>{p}</div>
                            <div className="text-xs text-muted-foreground">${PLAN_PRICES[p]}/seat</div>
                        </button>
                    ))}
                </div>

                <Label className="mb-1 block text-xs font-medium">Seats</Label>
                <Input
                    type="number"
                    min={1}
                    max={1000}
                    value={maxUsers}
                    onChange={(e) => setMaxUsers(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="mb-4"
                />

                <div className="mb-4 space-y-1 rounded-md bg-muted/40 p-3 text-xs">
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">Current monthly</span>
                        <span className="tabular-nums">${oldMonthly.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                        <span className="text-muted-foreground">New monthly</span>
                        <span className="tabular-nums">${newMonthly.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1 font-semibold">
                        <span>Prorated charge today</span>
                        <span className={cn("tabular-nums", proratedDelta < 0 ? "text-emerald-600" : null)}>
                            {proratedDelta < 0 ? "" : "$"}{proratedDelta.toFixed(2)}
                            {proratedDelta < 0 ? " credit" : ""}
                        </span>
                    </div>
                </div>

                {error ? (
                    <p className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        {error}
                    </p>
                ) : null}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={onClose} disabled={mutation.isPending}>
                        Cancel
                    </Button>
                    <Button size="sm" onClick={() => void submit()} disabled={noChange || mutation.isPending}>
                        {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Apply
                    </Button>
                </div>
            </div>
        </div>
    );
}
